import { TagColor } from 'components/SettingsSidebar/views/comments/TagColor';
import Constants, { Permissions } from 'config/Constants';
import { set } from 'features/app/appSlice';
import { isNumber } from 'lodash';
import { useGetEntityComments } from 'pages/comments/hooks/useGetEntityComments';
import { useGetArea } from 'pages/documents_v2/hooks/useGetArea';
import { useGetBlockLabelEntities } from 'pages/documents_v2/hooks/useGetBlockLabelEntities';
import { useGetDocument } from 'pages/documents_v2/hooks/useGetDocument';
import { ToolbarButton } from 'pages/documents_v2/views/edit_area/blocks/ToolbarButton';
import { useGetAreaId } from 'pages/documents_v2/views/edit_area/hooks/useGetAreaId';
import RestrictedContent from 'pages/global/RestrictedContent';
import { idToUri } from 'pages/global/UriHelper';
import { entityTypes } from 'pages/translation/config/Constants';
import { useMemo } from 'react';
import {
    Chat,
    ChatDots,
    ClockHistory,
    FileEarmarkBreak,
    Grid,
    Link45deg,
    TagsFill,
    UiChecksGrid,
} from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

/**
 * @param {import('models/documents.models').Block} block
 * @param {import('models/documents.models').Block} [parentBlock]
 * @param {number} index
 * @param {boolean} readOnly
 */
export function BlockLabels({ block, parentBlock, index, readOnly = false }) {
    const { t } = useTranslation('documents');
    const dispatch = useDispatch();
    const { documentId, areaId } = useParams();
    const entityLabelEntities = useGetBlockLabelEntities(parseInt(documentId), block.id);
    const document = useGetDocument();

    const hasLinkedBlocks = Object.hasOwn(block, 'linkedBlocks') && block.linkedBlocks.length > 0;
    const hasLabels = entityLabelEntities.length > 0;
    const showPreviousVersions =
        block.id && isNumber(document.clonedFromId) && block.type === Constants.blockTypes.text;

    const forceBreak = useMemo(() => {
        if (block) {
            const hasPropertiesForceNewPage = block.properties.forceNewPage && block.properties.forceNewPage === true;

            const hasPropertiesForceNewPageVariants =
                block.properties.forceNewPageVariants !== undefined && block.properties.forceNewPageVariants.length > 0;

            return hasPropertiesForceNewPage || hasPropertiesForceNewPageVariants;
        }
        return false;
    }, [block]);

    return (
        <>
            {document.type !== Constants.documentType.library && document.type !== Constants.documentType.model && (
                <BlockVariantWarnings
                    block={block}
                    parentBlock={parentBlock}
                    handleSettingsIconClick={handleSettingsIconClick}
                    isReadOnly={readOnly}
                />
            )}

            {hasLinkedBlocks && (
                <ToolbarButton
                    tooltip={t('document.navbar.main.editor.left.blocks.tooltips.viewLinkedArticles')}
                    onClick={handleShowLinkedBlockModalClick}
                    visible={true}
                    showDot={true}
                >
                    <Link45deg />
                </ToolbarButton>
            )}

            {forceBreak && (
                <ToolbarButton
                    tooltip={t('document.navbar.main.editor.left.blocks.tooltips.forcedBlock')}
                    onClick={() => handleSettingsIconClick()}
                    visible={true}
                    disabled={readOnly}
                >
                    <FileEarmarkBreak className="text-warning" />
                </ToolbarButton>
            )}

            {hasLabels && (
                <ToolbarButton
                    tooltip={t('document.navbar.main.editor.left.blocks.tooltips.labels')}
                    onClick={() => handleSettingsIconClick('labels')}
                    visible={true}
                    disabled={readOnly}
                >
                    <TagsFill />
                </ToolbarButton>
            )}

            {showPreviousVersions && (
                <ToolbarButton
                    tooltip={t('document.navbar.main.editor.left.blocks.tooltips.blockFromPreviewsVersion')}
                    onClick={handleShowPreviousVersionsClick}
                >
                    <ClockHistory />
                </ToolbarButton>
            )}

            {block.id && (
                <>
                    <RestrictedContent permission={Permissions.Comment['Read.All']}>
                        <CommentIcon
                            uri={idToUri(block.id, entityTypes.BLOCK)}
                            onClick={() => handleSettingsIconClick('comments')}
                        />
                    </RestrictedContent>
                </>
            )}
        </>
    );

    function handleSettingsIconClick(landingTab) {
        dispatch(
            set({
                key: 'sidebarContext',
                value: {
                    type: 'sidebarSettings',
                    context: {
                        entity: block,
                        entityType: entityTypes.BLOCK,
                        documentId: parseInt(documentId),
                        areaId: parseInt(areaId),
                        uri: idToUri(block.id, entityTypes.BLOCK),
                        parentUri: idToUri(parseInt(documentId), entityTypes.DOCUMENT),
                        landingTab,
                    },
                },
            }),
        );
    }

    function handleShowLinkedBlockModalClick() {
        dispatch(
            set({
                key: 'blockEditModal',
                value: {
                    eventKey: 'showLinkedBlockModal',
                    block,
                    blockIndex: index,
                },
            }),
        );
    }

    function handleShowPreviousVersionsClick() {
        dispatch(
            set({
                key: 'blockEditModal',
                value: {
                    eventKey: 'previousBlockVersions',
                    block,
                    blockIndex: index,
                },
            }),
        );
    }
}

function BlockVariantWarnings({ block, parentBlock, handleSettingsIconClick, isReadOnly }) {
    const { t } = useTranslation('documents');
    const areaId = useGetAreaId();
    const area = useGetArea(areaId);

    if (area?.properties?.isTemplate === true) {
        return null;
    }

    if (block.documentVariants.length <= 1) {
        return (
            <ToolbarButton
                tooltip={t('documents:document.navbar.main.editor.left.blocks.tooltips.noVariants')}
                onClick={() => handleSettingsIconClick('variants')}
                visible={true}
                disabled={isReadOnly}
                showDot={true}
                dotVariant="danger"
            >
                <Grid className="text-danger" />
            </ToolbarButton>
        );
    }

    const blockHasNotAllAreaVariants =
        area && area.documentVariants.some((areaVariantId) => block.documentVariants.includes(areaVariantId) === false);

    if (area && blockHasNotAllAreaVariants) {
        return (
            <ToolbarButton
                tooltip={t('documents:document.navbar.main.editor.left.blocks.tooltips.customVariants')}
                onClick={() => handleSettingsIconClick('variants')}
                visible={true}
                disabled={isReadOnly}
            >
                <UiChecksGrid className="text-warning" />
            </ToolbarButton>
        );
    }

    /**
     * Always show the variants icon for the 'info' block
     */
    if (
        parentBlock &&
        parentBlock.properties.type === Constants.blockTypes.textExplanation &&
        block.properties.textLayout === 'info'
    ) {
        return (
            <ToolbarButton
                tooltip={t('documents:document.navbar.main.editor.left.blocks.tooltips.variants')}
                onClick={() => handleSettingsIconClick('variants')}
                disabled={isReadOnly}
            >
                <UiChecksGrid />
            </ToolbarButton>
        );
    }

    return null;
}

function CommentIcon({ uri, onClick }) {
    const { t } = useTranslation();
    const { comments } = useGetEntityComments(uri);

    return (
        <ToolbarButton
            tooltip={t('documents:document.navbar.main.editor.left.blocks.settingsSidebar.tootltips.comments')}
            onClick={onClick}
            visible={comments.length > 0}
        >
            {comments.length === 0 ? <Chat /> : <ChatDots />}

            <TagColor uri={uri} comments={comments} />
        </ToolbarButton>
    );
}
